import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { ACCESS_TOKEN_KEY } from '../constants';
import { useTry } from '../hooks';
import { RestClient } from '../lib';

type Me =
  | {
      type: 'guest';
      name: string;
      email: string;
      tenant: string;
      tenantId: string;
    }
  | {
      type: 'admin';
      name: string;
      email: string;
    };

interface AuthContextProps {
  me: Me | null;
}

const AuthContext = createContext<AuthContextProps>({
  me: null,
});

function AuthProvider({ children }: { children: ReactNode }) {
  const { wrapReq } = useTry();
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    if (!me) {
      const fetchData = wrapReq(async () => {
        const res = await RestClient.post<Me>('/me', {
          accessToken: localStorage.getItem(ACCESS_TOKEN_KEY) ?? '',
        });
        setMe(res);

        if (!process.env.PENDO_API_KEY) return;
        const account: { id: string } & Record<string, string> =
          res.type === 'admin'
            ? { id: res.email, name: res.name }
            : {
                id: res.tenantId,
                name: res.tenant,
                // 本番環境運用担当者(PicMail)。本番環境確定前は未設定のため空文字を送る
                prodenvconfirmed:
                  (
                    await RestClient.get<{ operated?: string }>('/data', {
                      tenant: res.tenant,
                      type: 'operate',
                    })
                  ).operated ?? '',
              };
        window.pendo!.initialize({
          visitor: {
            id: res.email,
            email: res.email,
            name: res.name,
            app_language: 'ja',
          },
          account,
        });
      });

      void fetchData();
    }
  }, [me, wrapReq]);

  if (!me) return null;

  return <AuthContext.Provider value={{ me }}>{children}</AuthContext.Provider>;
}

function useAuth() {
  return useContext(AuthContext) as { me: Me };
}

function useTenant() {
  const { me } = useAuth();

  if (me.type !== 'guest') {
    throw new Error('Try to access tenant info from non-guest user');
  }

  return {
    tenantId: me.tenantId,
    tenant: me.tenant,
  };
}

export { AuthProvider, useAuth, useTenant };
export type { AuthContextProps, Me };
